import React, { useEffect, useState } from "react";
import { FaEuroSign, FaInfoCircle, FaDownload } from "react-icons/fa";
import DashboardCard from "./DashboardCard";
import api from "../../../services/api";
import { groupPeriodsByUser, resolveMonthlyCost } from "../../../utils/user_contract";
import toast from "react-hot-toast";
import { isInSelesquad } from "../../../utils/roles";

// Helper function to calculate actual working period for a user
const calculateWorkingPeriod = (user, dateRange) => {
  const periodStart = dateRange.startDate;
  const periodEnd = dateRange.endDate;

  // Determine actual start date (later of period start or user start date)
  let actualStart = periodStart;
  if (user.starting_date) {
    const userStartDate = new Date(user.starting_date);
    if (userStartDate > periodStart) {
      actualStart = userStartDate;
    }
  }

  // Determine actual end date (earlier of period end or user exit date)
  let actualEnd = periodEnd;
  if (user.exit_date) {
    const userExitDate = new Date(user.exit_date);
    if (userExitDate < periodEnd) {
      actualEnd = userExitDate;
    }
  }

  // If user hasn't started yet or already left before the period
  if (actualStart > periodEnd || actualEnd < periodStart) {
    return {
      monthsFraction: 0,
      description: "Not active in period",
      actualStart,
      actualEnd,
      daysWorked: 0,
    };
  }

  // Calculate working days
  const totalDays = Math.ceil((actualEnd - actualStart) / (1000 * 60 * 60 * 24)) + 1;
  const monthsFraction = totalDays / 30.44; // Average days per month

  // Generate description
  let description = "Full period";
  if (user.starting_date && new Date(user.starting_date) > periodStart) {
    description = `Started ${actualStart.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })}`;
  }
  if (user.exit_date && new Date(user.exit_date) < periodEnd) {
    description = `Left ${actualEnd.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })}`;
  }
  if (user.starting_date && user.exit_date && new Date(user.starting_date) > periodStart && new Date(user.exit_date) < periodEnd) {
    description = `${actualStart.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })} - ${actualEnd.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
    })}`;
  }

  return {
    monthsFraction: Math.max(0, monthsFraction),
    description,
    actualStart,
    actualEnd,
    daysWorked: totalDays,
  };
};

const ProfitabilityCard = ({ user, filters, organisation, businessUnit }) => {
  // Only show this card for selesquad members (or when showing whole company)
  if (user && !isInSelesquad(user))  return null;

  const [profitabilityData, setProfitabilityData] = useState({
    totalCosts: 0,
    totalRevenue: 0,
    totalInvestment: 0,
    totalBenefit: 0,
    totalMargin: 0,
    // Detailed breakdown
    monthlyCosts: 0,
    dailyCosts: 0,
    teamMembers: [],
    revenueByType: {},
    calculationDetails: {},
    // Occupation data
    occupationData: {
      teamOccupation: [],
      averageOccupation: 0,
      totalWorkingHours: 0,
      totalAvailableHours: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [timePeriod, setTimePeriod] = useState("current");

  // Generate time period options
  const getTimePeriodOptions = () => {
    const currentDate = new Date();
    const currentMonthName = currentDate.toLocaleString("default", { month: "long", year: "numeric" });
    const lastMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const lastMonthName = lastMonthDate.toLocaleString("default", { month: "long", year: "numeric" });
    const twoMonthsAgoDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 2, 1);
    const twoMonthsAgoName = twoMonthsAgoDate.toLocaleString("default", { month: "long", year: "numeric" });
    const threeMonthsAgoDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
    const threeMonthsAgoName = threeMonthsAgoDate.toLocaleString("default", { month: "long", year: "numeric" });
    const currentYear = currentDate.getFullYear();
    const lastYear = currentYear - 1;

    return [
      { value: "current", label: currentMonthName },
      { value: "lastMonth", label: lastMonthName },
      { value: "twoMonthsAgo", label: twoMonthsAgoName },
      { value: "threeMonthsAgo", label: threeMonthsAgoName },
      { value: "last3", label: "Last 3 months" },
      { value: "last6", label: "Last 6 months" },
      { value: "sept_dec_2025", label: "⭐ Sept-Dec 2025" },
      { value: "q1", label: `Q1 ${currentYear}` },
      { value: "q2", label: `Q2 ${currentYear}` },
      { value: "q3", label: `Q3 ${currentYear}` },
      { value: "q4", label: `Q4 ${currentYear}` },
      { value: "q4_last_year", label: `Q4 ${lastYear}` },
      { value: "currentYear", label: `Year ${currentYear}` },
    ];
  };

  // Calculate date range based on selected period
  const getDateRange = () => {
    const currentDate = new Date();

    switch (timePeriod) {
      case "current":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 1)),
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1, 0)),
          isRange: false,
        };
      case "twoMonthsAgo":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() - 2, 1)),
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() - 1, 0)),
          isRange: false,
        };
      case "threeMonthsAgo":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() - 3, 1)),
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() - 2, 0)),
          isRange: false,
        };
      case "lastMonth":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() - 1, 1)),
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 0)),
          isRange: false,
        };
      case "last3":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() - 2, 1)),
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1, 0)),
          isRange: true,
        };
      case "last6":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() - 5, 1)),
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1, 0)),
          isRange: true,
        };
      case "q1":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 0, 1)), // January 1st
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 2, 31)), // March 31st
          isRange: true,
        };
      case "q2":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 3, 1)), // April 1st
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 5, 30)), // June 30th
          isRange: true,
        };
      case "q3":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 6, 1)), // July 1st
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 8, 30)), // September 30th
          isRange: true,
        };
      case "q4":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 9, 1)), // October 1st
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 11, 31)), // December 31st
          isRange: true,
        };
      case "q4_last_year":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear() - 1, 9, 1)), // October 1st last year
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear() - 1, 11, 31)), // December 31st last year
          isRange: true,
        };
      case "sept_dec_2025":
        return {
          startDate: new Date(Date.UTC(2025, 8, 1)), // September 1st 2025
          endDate: new Date(Date.UTC(2025, 11, 31)), // December 31st 2025
          isRange: true,
        };
      case "currentYear":
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 0, 1)), // January 1st of current year
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), 11, 31)), // December 31st of current year
          isRange: true,
        };
      default:
        return {
          startDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 1)),
          endDate: new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1, 0)),
          isRange: false,
        };
    }
  };

  useEffect(() => {
    const fetchProfitabilityData = async () => {
      try {
        setLoading(true);

        // Get date range first to determine which users to include
        const dateRange = getDateRange();

        // Build base query for user search
        const baseUserQuery = {
          role: ["admin", "user", "super_admin"],
          contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
        };

        // Add manager filter only if a user is selected
        if (user) {
          baseUserQuery.manager_id = user._id;
          baseUserQuery.managees_of_managees = filters.manageesOfManagees;
        }

        // Filter by business unit
        if (businessUnit._id !== "ALL") {
          baseUserQuery.business_unit_id = businessUnit._id;
        } else if (organisation?._id && organisation._id !== "ALL") {
          baseUserQuery.organisation_id = organisation._id;
        }

        // Fetch current team members (active)
        const { data: activeUsersData, ok: okActive } = await api.post(`/user/search`, {
          ...baseUserQuery,
          status: "active",
        });

        // Fetch former team members (inactive) who might have had activities in the period
        const { data: inactiveUsersData, ok: okInactive } = await api.post(`/user/search`, {
          ...baseUserQuery,
          status: "inactive",
        });

        if (!okActive || !okInactive) {
          console.error("Error fetching users for profitability");
          setLoading(false);
          return;
        }

        // Dated compensation periods → resolve each member's monthly cost as of the period end.
        const { data: contractsData } = await api.post("/user_contract/search", businessUnit._id !== "ALL" ? { business_unit_id: businessUnit._id, limit: 10000 } : { limit: 10000 });
        const periodsByUser = groupPeriodsByUser(contractsData || []);

        // Combine active and inactive users
        let allPotentialMembers = [...(activeUsersData?.users || []), ...(inactiveUsersData?.users || [])];

        // Add the manager themselves to the potential members (if not already included and if user is selected)
        let managerAlreadyIncluded = true; // Default to true when no user is selected
        if (user) {
          managerAlreadyIncluded = allPotentialMembers.some((member) => member._id === user._id);
          if (!managerAlreadyIncluded) {
            allPotentialMembers.push(user);
          }
        }

        // Fetch activities for the selected period (including all potential team members)
        const activityQuery = {
          userIds: allPotentialMembers.map((u) => u._id),
        };

        if (dateRange.isRange) {
          // For multi-month periods, use date range
          activityQuery.dateFrom = dateRange.startDate.getTime();
          activityQuery.dateTo = dateRange.endDate.getTime();
        } else {
          // For single month, use the date field
          activityQuery.date = dateRange.startDate.getTime();
        }

        const { data: activitiesData, ok: okActivities } = await api.post(`/activity/search`, activityQuery);

        if (!okActivities) {
          console.error("Error fetching activities for profitability");
          setLoading(false);
          return;
        }

        // Filter out "Unavailable" activities as they don't represent real work
        const validActivities = (activitiesData || []).filter((activity) => activity.projectName !== "Unavailable" && activity.budgetName !== "Unavailable");

        // Calculate detailed costs breakdown
        let totalCosts = 0;
        let monthlyCosts = 0;
        let dailyCosts = 0;
        const teamMembersDetails = [];

        // Define leave/time-off project names
        const leaveProjects = ["Leave Paid", "RTT", "Leave Unpaid", "Arret Maladie", "Holiday", "Holliday", "Unavailable"];

        // Filter team members to only include those who were active during the period or have activities
        const relevantTeamMembers = allPotentialMembers.filter((member) => {
          const workingPeriod = calculateWorkingPeriod(member, dateRange);
          const hasActivities = validActivities.some((activity) => activity.userId === member._id);

          // For former employees, only include if they have activities in the period
          if (member.status === "inactive") {
            return hasActivities;
          }

          // For active employees, include if they worked during the period OR have activities
          return workingPeriod.monthsFraction > 0 || hasActivities;
        });

        for (const teamMember of relevantTeamMembers) {
          const memberActivities = validActivities.filter((activity) => activity.userId === teamMember._id);
          let memberCost = 0;
          let costType = "";

          if (teamMember.payment_type === "MONTHLY") {
            // For monthly employees, calculate cost based on actual working period
            const baseMonthlyCost = resolveMonthlyCost(periodsByUser, teamMember, dateRange.endDate) || teamMember.payment_value || 0;

            // Calculate actual working period considering entrance/exit dates
            const actualWorkingPeriod = calculateWorkingPeriod(teamMember, dateRange);
            memberCost = baseMonthlyCost * actualWorkingPeriod.monthsFraction;

            monthlyCosts += memberCost;
            costType = `Monthly (${actualWorkingPeriod.description})`;
          } else {
            // For daily/hourly employees, calculate cost from all valid activities (including leave)
            // but exclude "Unavailable" time
            const allMemberActivities = activitiesData.filter(
              (activity) => activity.userId === teamMember._id && activity.projectName !== "Unavailable" && activity.budgetName !== "Unavailable",
            );
            memberCost = allMemberActivities.reduce((acc, activity) => acc + (activity.cost || 0), 0);
            dailyCosts += memberCost;
            costType = "Activity-based";
          }

          totalCosts += memberCost;

          // Calculate member revenue breakdown (only from revenue-generating activities)
          // Exclude leave/time-off activities as they don't generate revenue
          const revenueGeneratingActivities = memberActivities.filter((activity) => !leaveProjects.includes(activity.projectName));

          const memberRevenueByType = {
            project: 0,
            prediction: 0,
            admin: 0,
            invest: 0,
            overbudget: 0,
            leave: 0, // Track leave separately
          };

          // Calculate revenue from work activities
          revenueGeneratingActivities.forEach((activity) => {
            const budgetType = activity.budgetType || "other";
            if (memberRevenueByType.hasOwnProperty(budgetType)) {
              memberRevenueByType[budgetType] += activity.value || 0;
            }
          });

          // Track leave activities separately (cost but no revenue)
          const leaveActivities = memberActivities.filter((activity) => leaveProjects.includes(activity.projectName));
          const leaveCost = leaveActivities.reduce((acc, activity) => acc + (activity.cost || 0), 0);

          const memberTotalRevenue = revenueGeneratingActivities.reduce((acc, activity) => acc + (activity.value || 0), 0);
          const memberBillableRevenue = revenueGeneratingActivities
            .filter((activity) => activity.budgetType !== "invest" && activity.budgetType !== "admin")
            .reduce((acc, activity) => acc + (activity.value || 0), 0);

          const memberBenefit = memberBillableRevenue - memberCost;
          const memberMargin = memberBillableRevenue > 0 ? (memberBenefit / memberBillableRevenue) * 100 : 0;

          const workingPeriod = calculateWorkingPeriod(teamMember, dateRange);

          teamMembersDetails.push({
            name: teamMember.name,
            avatar: teamMember.avatar,
            cost: memberCost,
            costType: costType,
            activitiesCount: memberActivities.length,
            totalHours: memberActivities.reduce((acc, activity) => acc + (activity.total || 0), 0),
            totalRevenue: memberTotalRevenue,
            billableRevenue: memberBillableRevenue,
            benefit: memberBenefit,
            margin: memberMargin,
            revenueByType: memberRevenueByType,
            activities: memberActivities,
            leaveHours: leaveActivities.reduce((acc, activity) => acc + (activity.total || 0), 0),
            leaveCost: leaveCost,
            workingHours: revenueGeneratingActivities.reduce((acc, activity) => acc + (activity.total || 0), 0),
            workingPeriod: workingPeriod,
            startingDate: teamMember.starting_date,
            exitDate: teamMember.exit_date,
            isActive: teamMember.status === "active",
          });
        }

        // Calculate revenue breakdown by budget type (excluding leave activities)
        const workActivities = validActivities.filter((activity) => !leaveProjects.includes(activity.projectName));

        const revenueByType = {
          project: 0,
          prediction: 0,
          admin: 0,
          invest: 0,
          overbudget: 0,
        };

        workActivities.forEach((activity) => {
          const budgetType = activity.budgetType || "other";
          if (revenueByType.hasOwnProperty(budgetType)) {
            revenueByType[budgetType] += activity.value || 0;
          }
        });

        // Calculate revenue (CA) - exclude invest, admin, and leave activities
        const revenueActivities = workActivities.filter((activity) => activity.budgetType !== "invest" && activity.budgetType !== "admin");
        const totalRevenue = revenueActivities.reduce((acc, activity) => acc + (activity.value || 0), 0);

        // Calculate investment - only invest budget type
        const investmentActivities = workActivities.filter((activity) => activity.budgetType === "invest");
        const totalInvestment = investmentActivities.reduce((acc, activity) => acc + (activity.value || 0), 0);

        // Calculate benefit (Revenue - Costs)
        const totalBenefit = totalRevenue - totalCosts;

        // Calculate margin percentage
        const totalMargin = totalRevenue > 0 ? (totalBenefit / totalRevenue) * 100 : 0;

        // Calculate leave statistics
        const leaveActivities = validActivities.filter((activity) => leaveProjects.includes(activity.projectName));
        const totalLeaveHours = leaveActivities.reduce((acc, activity) => acc + (activity.total || 0), 0);
        const totalLeaveCost = leaveActivities.reduce((acc, activity) => acc + (activity.cost || 0), 0);

        // Calculate entrance/exit adjustments
        const membersWithAdjustments = teamMembersDetails.filter((member) => member.workingPeriod.description !== "Full period");

        // Calculate occupation data from activities
        const calculateOccupationData = () => {
          const teamOccupation = [];
          let totalWorkingHours = 0;
          let totalAvailableHours = 0;

          for (const member of relevantTeamMembers) {
            const memberActivities = validActivities.filter((activity) => activity.userId === member._id);
            
            // Calculate working hours (excluding leave activities AND admin activities, 
            // BUT include admin activities that are actually leave activities)
            const workingActivities = memberActivities.filter((activity) => {
              // Exclude leave projects
              if (leaveProjects.includes(activity.projectName)) {
                return false;
              }
              
              // Exclude admin activities UNLESS they are leave activities
              if (activity.budgetType === "admin") {
                // Check if this admin activity is actually a leave activity
                const isLeaveActivity = leaveProjects.some(leaveProject => 
                  activity.projectName.toLowerCase().includes(leaveProject.toLowerCase()) ||
                  activity.budgetName.toLowerCase().includes(leaveProject.toLowerCase())
                );
                return isLeaveActivity; // Include if it's a leave activity, exclude if it's regular admin
              }
              
              // Include all other activities (project, prediction, overbudget)
              // Exclude invest activities (they go in grey zone)
              return activity.budgetType !== "invest";
            });
            const workingHours = workingActivities.reduce((acc, activity) => acc + (activity.total || 0), 0);
            
            // Calculate investment hours (grey zone)
            const investmentActivities = memberActivities.filter((activity) => 
              activity.budgetType === "invest" && !leaveProjects.includes(activity.projectName)
            );
            const investmentHours = investmentActivities.reduce((acc, activity) => acc + (activity.total || 0), 0);
            
            // Calculate available hours based on working period
            const workingPeriod = calculateWorkingPeriod(member, dateRange);
            const workingDaysInPeriod = workingPeriod.daysWorked;
            
            // Calculate actual working days in the month (excluding weekends)
            const getWorkingDaysInMonth = (date) => {
              const year = date.getFullYear();
              const month = date.getMonth();
              const daysInMonth = new Date(year, month + 1, 0).getDate();
              let workingDays = 0;
              
              for (let day = 1; day <= daysInMonth; day++) {
                const currentDate = new Date(year, month, day);
                const dayOfWeek = currentDate.getDay();
                // Monday = 1, Tuesday = 2, ..., Friday = 5 (exclude weekends)
                if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                  workingDays++;
                }
              }
              return workingDays;
            };
            
            // Use actual working days in the month, not just the period days
            const actualWorkingDaysInMonth = getWorkingDaysInMonth(dateRange.startDate);
            
            // For multi-month periods (like Q3), calculate working days across all months
            let totalWorkingDaysInPeriod = 0;
            if (dateRange.isRange) {
              // Calculate working days across the entire period
              const startDate = new Date(dateRange.startDate);
              const endDate = new Date(dateRange.endDate);
              
              for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
                const dayOfWeek = date.getDay();
                // Monday = 1, Tuesday = 2, ..., Friday = 5 (exclude weekends)
                if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                  totalWorkingDaysInPeriod++;
                }
              }
            } else {
              // For single month, use the month's working days
              totalWorkingDaysInPeriod = actualWorkingDaysInMonth;
            }
            
            const availableHours = Math.min(workingDaysInPeriod, totalWorkingDaysInPeriod) * 8;
            
            // Calculate occupation rate
            const occupationRate = availableHours > 0 ? (workingHours / availableHours) * 100 : 0;
            
            totalWorkingHours += workingHours;
            totalAvailableHours += availableHours;
            
            teamOccupation.push({
              name: member.name,
              avatar: member.avatar,
              workingHours: workingHours,
              investmentHours: investmentHours,
              availableHours: availableHours,
              occupationRate: occupationRate,
              workingPeriod: workingPeriod,
              isActive: member.status === "active",
            });
          }

          const averageOccupation = totalAvailableHours > 0 ? (totalWorkingHours / totalAvailableHours) * 100 : 0;

          return {
            teamOccupation,
            averageOccupation,
            totalWorkingHours,
            totalAvailableHours,
          };
        };

        const occupationData = calculateOccupationData();

        // Calculate project-level profitability with cost allocation
        const calculateProjectProfitability = () => {
          const projectMap = new Map();

          // Group activities by project
          workActivities.forEach((activity) => {
            const projectKey = activity.projectId || activity.projectName || "Unknown";
            const projectName = activity.projectName || "Unknown Project";

            if (!projectMap.has(projectKey)) {
              projectMap.set(projectKey, {
                projectId: activity.projectId || "",
                projectName: projectName,
                activities: [],
                totalHours: 0,
                calculatedRevenue: 0,
                invoicedRevenue: 0,
                allocatedCosts: 0,
                teamMembers: new Set(),
                budgetTypes: new Set(),
                invoiceIds: new Set(),
              });
            }

            const project = projectMap.get(projectKey);
            project.activities.push(activity);
            project.totalHours += activity.total || 0;
            project.calculatedRevenue += activity.value || 0;
            if (activity.userId) project.teamMembers.add(activity.userId);
            if (activity.budgetType) project.budgetTypes.add(activity.budgetType);
            if (activity.invoiceId) project.invoiceIds.add(activity.invoiceId);
          });

          // Collect unique invoice IDs to fetch
          const allInvoiceIds = Array.from(new Set(workActivities.filter((a) => a.invoiceId).map((a) => a.invoiceId)));

          // Fetch invoices if any exist
          return { projectMap, allInvoiceIds };
        };

        const { projectMap, allInvoiceIds } = calculateProjectProfitability();

        // Fetch invoices for invoiced amounts
        let invoicesData = [];
        if (allInvoiceIds.length > 0) {
          try {
            const invoicesPromises = allInvoiceIds.map((invoiceId) => api.get(`/invoice/${invoiceId}`).catch(() => ({ ok: false })));
            const invoicesResults = await Promise.all(invoicesPromises);
            invoicesData = invoicesResults.filter((result) => result.ok && result.data).map((result) => result.data);
          } catch (error) {
            console.error("Error fetching invoices:", error);
          }
        }

        // Create invoice lookup map
        const invoiceMap = new Map();
        invoicesData.forEach((invoice) => {
          invoiceMap.set(invoice._id, invoice);
        });

        // Allocate costs to projects based on hours worked
        const projectProfitability = [];
        
        // First, calculate total working hours per employee (for cost allocation)
        const employeeTotalWorkingHours = new Map();
        relevantTeamMembers.forEach((member) => {
          const memberActivities = workActivities.filter((activity) => activity.userId === member._id);
          const totalWorkingHours = memberActivities.reduce((acc, activity) => acc + (activity.total || 0), 0);
          employeeTotalWorkingHours.set(member._id, totalWorkingHours);
        });

        // Allocate costs per project
        for (const [projectKey, project] of projectMap.entries()) {
          let allocatedCosts = 0;
          const memberContributions = new Map();

          // Group activities by member for this project
          project.activities.forEach((activity) => {
            if (!activity.userId) return;
            if (!memberContributions.has(activity.userId)) {
              memberContributions.set(activity.userId, { hours: 0, member: null });
            }
            const contribution = memberContributions.get(activity.userId);
            contribution.hours += activity.total || 0;
            if (!contribution.member) {
              contribution.member = relevantTeamMembers.find((m) => m._id === activity.userId);
            }
          });

          // Allocate monthly costs proportionally based on hours
          memberContributions.forEach((contribution, userId) => {
            const member = contribution.member;
            if (!member) return;

            const projectHours = contribution.hours;
            const totalMemberHours = employeeTotalWorkingHours.get(userId) || 1;

            if (member.payment_type === "MONTHLY") {
              const baseMonthlyCost = resolveMonthlyCost(periodsByUser, member, dateRange.endDate) || member.payment_value || 0;
              const actualWorkingPeriod = calculateWorkingPeriod(member, dateRange);
              const totalMemberCost = baseMonthlyCost * actualWorkingPeriod.monthsFraction;
              
              // Allocate cost proportionally: (project hours / total member hours) * member cost
              const allocatedCost = totalMemberHours > 0 ? (projectHours / totalMemberHours) * totalMemberCost : 0;
              allocatedCosts += allocatedCost;
            } else {
              // For daily/hourly employees, costs are already in activities
              const projectActivities = project.activities.filter((a) => a.userId === userId);
              const activityCost = projectActivities.reduce((acc, activity) => acc + (activity.cost || 0), 0);
              allocatedCosts += activityCost;
            }
          });

          // Calculate invoiced revenue from linked invoices
          // For person-project level: calculate proportional share based on this person's activity values in the invoice
          let invoicedRevenue = 0;
          const uniqueInvoiceIds = new Set();
          project.activities.forEach((activity) => {
            if (activity.invoiceId) {
              uniqueInvoiceIds.add(activity.invoiceId);
            }
          });

          uniqueInvoiceIds.forEach((invoiceId) => {
            const invoice = invoiceMap.get(invoiceId);
            if (invoice && invoice.total) {
              // Get all activities in this invoice (across all people/projects)
              const invoiceAllActivities = workActivities.filter((a) => a.invoiceId === invoiceId);
              const invoiceTotalValue = invoiceAllActivities.reduce((sum, a) => sum + (a.value || 0), 0);
              
              // Get this person's activities for this project in this invoice
              const personProjectInvoiceActivities = project.activities.filter((a) => a.invoiceId === invoiceId);
              const personProjectInvoiceValue = personProjectInvoiceActivities.reduce((sum, a) => sum + (a.value || 0), 0);
              
              // Calculate proportional share: (person's activity value / total invoice activity value) * invoice total
              if (invoiceTotalValue > 0) {
                const personShare = (personProjectInvoiceValue / invoiceTotalValue) * invoice.total;
                invoicedRevenue += personShare;
              }
            }
          });

          // Calculate project metrics
          const projectBenefit = project.calculatedRevenue - allocatedCosts;
          const projectMargin = project.calculatedRevenue > 0 ? (projectBenefit / project.calculatedRevenue) * 100 : 0;

          // Invoiced vs calculated comparison
          const revenueDifference = invoicedRevenue - project.calculatedRevenue;
          const invoicedMargin = invoicedRevenue > 0 ? ((invoicedRevenue - allocatedCosts) / invoicedRevenue) * 100 : 0;

          projectProfitability.push({
            projectId: project.projectId,
            projectName: project.projectName,
            activitiesCount: project.activities.length,
            totalHours: project.totalHours,
            teamMemberCount: project.teamMembers.size,
            budgetTypes: Array.from(project.budgetTypes),
            calculatedRevenue: project.calculatedRevenue,
            invoicedRevenue: invoicedRevenue,
            revenueDifference: revenueDifference,
            allocatedCosts: allocatedCosts,
            benefit: projectBenefit,
            margin: projectMargin,
            invoicedMargin: invoicedMargin,
            invoiceIds: Array.from(project.invoiceIds),
            memberContributions: Array.from(memberContributions.entries()).map(([userId, contrib]) => ({
              userId,
              memberName: contrib.member?.name || "",
              hours: contrib.hours,
            })),
          });
        }

        // Sort projects by calculated revenue descending
        projectProfitability.sort((a, b) => b.calculatedRevenue - a.calculatedRevenue);

        // Calculate person-level profitability with project breakdown
        const personProfitability = [];

        // Group activities by person
        relevantTeamMembers.forEach((member) => {
          const memberActivities = workActivities.filter((activity) => activity.userId === member._id);
          if (memberActivities.length === 0) return;

          // Calculate member costs
          let totalMemberCost = 0;
          let costType = "";
          let baseMonthlyCost = 0;
          let monthsFraction = 0;

          if (member.payment_type === "MONTHLY") {
            baseMonthlyCost = resolveMonthlyCost(periodsByUser, member, dateRange.endDate) || member.payment_value || 0;
            const actualWorkingPeriod = calculateWorkingPeriod(member, dateRange);
            monthsFraction = actualWorkingPeriod.monthsFraction;
            totalMemberCost = baseMonthlyCost * monthsFraction;
            costType = `Monthly (${actualWorkingPeriod.description})`;
          } else {
            // For daily/hourly employees, costs come from activities
            totalMemberCost = memberActivities.reduce((acc, activity) => acc + (activity.cost || 0), 0);
            costType = "Activity-based";
          }

          // Get base TJM Sell from user (for comparison with overwritten)
          const baseTjms = member.tjms || member.user_tjms || 0;

          // Group member activities by project
          const projectBreakdown = new Map();
          memberActivities.forEach((activity) => {
            const projectKey = activity.projectId || activity.projectName || "Unknown";
            const projectName = activity.projectName || "Unknown Project";

            if (!projectBreakdown.has(projectKey)) {
              projectBreakdown.set(projectKey, {
                projectId: activity.projectId || "",
                projectName: projectName,
                projectType: activity.projectType || "",
                hours: 0,
                daysWorked: 0,
                calculatedRevenue: 0,
                invoicedRevenue: 0,
                baseTjmsRevenue: 0, // Revenue using base TJM
                overwrittenTjms: null, // Track if TJM was overwritten
                activities: [],
              });
            }

            const project = projectBreakdown.get(projectKey);
            const activityHours = activity.total || 0;
            const activityDays = activityHours / 8;
            project.hours += activityHours;
            project.daysWorked += activityDays;
            project.calculatedRevenue += activity.value || 0;
            // Store projectType if not already set, or if it becomes "admin"
            if (!project.projectType && activity.projectType) {
              project.projectType = activity.projectType;
            } else if (activity.projectType === "ADMIN" || activity.projectType === "admin") {
              project.projectType = activity.projectType;
            }
            
            // Calculate what revenue would be with base TJM
            const activityBaseTjmsRevenue = activityDays * baseTjms;
            project.baseTjmsRevenue += activityBaseTjmsRevenue;
            
            // Track overwritten TJM (the actual TJM used in activity)
            const activityTjms = activity.userTjms || activity.user_tjms || 0;
            if (activityTjms && activityTjms !== baseTjms) {
              // TJM was overwritten
              if (project.overwrittenTjms === null || project.overwrittenTjms === baseTjms) {
                project.overwrittenTjms = activityTjms;
              }
            }
            
            project.activities.push(activity);

            // Track invoiced revenue - will be calculated later per person-project
          });

          // Calculate total hours for cost allocation
          const totalMemberHours = memberActivities.reduce((acc, activity) => acc + (activity.total || 0), 0);

          // Allocate costs to projects proportionally based on hours
          const projectAllocations = [];
          let totalAllocatedCost = 0;

          projectBreakdown.forEach((project, projectKey) => {
            // For monthly employees, allocate cost proportionally
            let allocatedCost = 0;
            if (member.payment_type === "MONTHLY" && totalMemberHours > 0) {
              allocatedCost = (project.hours / totalMemberHours) * totalMemberCost;
            } else {
              // For daily/hourly, costs are already in activities
              allocatedCost = project.activities.reduce((acc, activity) => acc + (activity.cost || 0), 0);
            }

            totalAllocatedCost += allocatedCost;

            const projectBenefit = project.calculatedRevenue - allocatedCost;
            const projectMargin = project.calculatedRevenue > 0 ? (projectBenefit / project.calculatedRevenue) * 100 : 0;

            // Calculate average overwritten TJM for this project
            // Set TJM to 0 for admin projects or leave/timeoff projects
            const adminProjectNames = ["accounting", "selego", "admin"];
            const leaveProjectNames = ["leave paid", "rtt", "leave unpaid", "arret maladie", "holiday", "holliday", "timeoff"];
            
            const projectNameLower = (project.projectName || "").toLowerCase().trim();
            const projectTypeLower = (project.projectType || "").toLowerCase();
            
            // Check if project is admin by name, projectType (case-insensitive), or budget type
            const allActivitiesAreAdmin = project.activities.length > 0 && 
              project.activities.every((act) => (act.budgetType || "").toLowerCase() === "admin");
            const anyActivityIsAdmin = project.activities.some((act) => (act.budgetType || "").toLowerCase() === "admin");
            const anyActivityProjectTypeIsAdmin = project.activities.some((act) => {
              const actProjectType = (act.projectType || "").toLowerCase();
              return actProjectType === "admin";
            });
            const projectTypeIsAdmin = projectTypeLower === "admin";
            const projectNameIsAdmin = adminProjectNames.includes(projectNameLower);
            const anyActivityProjectNameIsAdmin = project.activities.some((act) => 
              adminProjectNames.includes((act.projectName || "").toLowerCase().trim())
            );
            
            const isAdminProject = projectTypeIsAdmin || projectNameIsAdmin || anyActivityProjectNameIsAdmin || allActivitiesAreAdmin || anyActivityIsAdmin || anyActivityProjectTypeIsAdmin;
            
            // Check if project is leave/timeoff by name or projectType (case-insensitive)
            const projectTypeIsTimeoff = projectTypeLower === "timeoff";
            const anyActivityProjectTypeIsTimeoff = project.activities.some((act) => {
              const actProjectType = (act.projectType || "").toLowerCase();
              return actProjectType === "timeoff";
            });
            
            const isLeaveProject = 
              projectTypeIsTimeoff ||
              anyActivityProjectTypeIsTimeoff ||
              leaveProjectNames.includes(projectNameLower) ||
              project.activities.some((act) => 
                leaveProjectNames.includes((act.projectName || "").toLowerCase().trim())
              );
            
            const shouldSetTjmToZero = isAdminProject || isLeaveProject;
            
            let avgOverwrittenTjms = shouldSetTjmToZero ? 0 : baseTjms;
            
            if (!shouldSetTjmToZero && project.overwrittenTjms !== null && project.overwrittenTjms !== baseTjms) {
              // Calculate weighted average of overwritten TJMs
              const tjmsSum = project.activities.reduce((sum, act) => {
                const actTjms = act.userTjms || act.user_tjms || baseTjms;
                const actDays = (act.total || 0) / 8;
                return sum + (actTjms * actDays);
              }, 0);
              avgOverwrittenTjms = project.daysWorked > 0 ? tjmsSum / project.daysWorked : baseTjms;
            }

            projectAllocations.push({
              projectId: project.projectId,
              projectName: project.projectName,
              hours: project.hours,
              daysWorked: project.daysWorked,
              percentageOfTime: totalMemberHours > 0 ? (project.hours / totalMemberHours) * 100 : 0,
              allocatedCost: allocatedCost,
              calculatedRevenue: project.calculatedRevenue,
              invoicedRevenue: project.invoicedRevenue,
              baseTjms: baseTjms,
              overwrittenTjms: avgOverwrittenTjms,
              baseTjmsRevenue: project.baseTjmsRevenue,
              benefit: projectBenefit,
              margin: projectMargin,
              activitiesCount: project.activities.length,
            });
          });

          // Calculate person totals
          const totalRevenue = memberActivities
            .filter((activity) => activity.budgetType !== "invest" && activity.budgetType !== "admin")
            .reduce((acc, activity) => acc + (activity.value || 0), 0);

          const totalInvoicedRevenue = Array.from(projectBreakdown.values()).reduce(
            (sum, project) => sum + project.invoicedRevenue,
            0
          );

          const totalBenefit = totalRevenue - totalMemberCost;
          const totalMargin = totalRevenue > 0 ? (totalBenefit / totalRevenue) * 100 : 0;

          // Sort projects by revenue descending
          projectAllocations.sort((a, b) => b.calculatedRevenue - a.calculatedRevenue);

          personProfitability.push({
            userId: member._id,
            name: member.name,
            avatar: member.avatar,
            isActive: member.status === "active",
            paymentType: member.payment_type,
            costType: costType,
            baseMonthlyCost: baseMonthlyCost,
            monthsFraction: monthsFraction,
            baseTjms: baseTjms,
            totalCost: totalMemberCost,
            totalHours: totalMemberHours,
            totalRevenue: totalRevenue,
            totalInvoicedRevenue: totalInvoicedRevenue,
            totalBenefit: totalBenefit,
            totalMargin: totalMargin,
            totalAllocatedCost: totalAllocatedCost,
            projectsCount: projectAllocations.length,
            activitiesCount: memberActivities.length,
            projectBreakdown: projectAllocations,
            workingPeriod: calculateWorkingPeriod(member, dateRange),
            startingDate: member.starting_date,
            exitDate: member.exit_date,
          });
        });

        // Sort persons by total revenue descending
        personProfitability.sort((a, b) => b.totalRevenue - a.totalRevenue);

        const calculationDetails = {
          totalActivities: activitiesData.length,
          validActivities: validActivities.length,
          workActivities: workActivities.length,
          leaveActivities: leaveActivities.length,
          revenueActivities: revenueActivities.length,
          investmentActivities: investmentActivities.length,
          monthlyEmployees: relevantTeamMembers.filter((u) => u.payment_type === "MONTHLY").length,
          dailyEmployees: relevantTeamMembers.filter((u) => u.payment_type !== "MONTHLY").length,
          totalLeaveHours: totalLeaveHours,
          totalLeaveCost: totalLeaveCost,
          includesManager: !managerAlreadyIncluded,
          timePeriod: getTimePeriodOptions().find((opt) => opt.value === timePeriod)?.label,
          dateRange: dateRange,
          membersWithAdjustments: membersWithAdjustments.length,
          adjustmentDetails: membersWithAdjustments.map((m) => ({
            name: m.name,
            description: m.workingPeriod.description,
            daysWorked: m.workingPeriod.daysWorked,
          })),
          activeMembers: activeUsersData.users.length,
          formerMembers: inactiveUsersData.users.filter((member) => relevantTeamMembers.some((relevant) => relevant._id === member._id)).length,
        };

        setProfitabilityData({
          totalCosts: totalCosts,
          totalRevenue: totalRevenue,
          totalInvestment: totalInvestment,
          totalBenefit: totalBenefit,
          totalMargin: totalMargin,
          monthlyCosts: monthlyCosts,
          dailyCosts: dailyCosts,
          teamMembers: teamMembersDetails,
          revenueByType: revenueByType,
          calculationDetails: calculationDetails,
          occupationData: occupationData,
          allActivities: validActivities,
          dateRange: dateRange,
          projectProfitability: projectProfitability,
          personProfitability: personProfitability,
          invoicesData: invoicesData,
          invoiceMap: Object.fromEntries(invoiceMap),
        });
      } catch (error) {
        console.error(error);
        toast.error("Error fetching profitability data");
      } finally {
        setLoading(false);
      }
    };

    fetchProfitabilityData();
  }, [user?._id, filters, timePeriod]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getMarginColor = (margin) => {
    if (margin >= 30) return "text-green-600";
    if (margin >= 15) return "text-yellow-600";
    return "text-red-600";
  };

  const getBenefitColor = (benefit) => {
    if (benefit >= 0) return "text-green-600";
    return "text-red-600";
  };

  const exportToCSV = () => {
    if (!profitabilityData.teamMembers || profitabilityData.teamMembers.length === 0) {
      toast.error("No data to export");
      return;
    }

    const timePeriodLabel = getTimePeriodOptions().find((opt) => opt.value === timePeriod)?.label || timePeriod;
    const csvRows = [];

    // Header Section
    csvRows.push(["PROFITABILITY REPORT"]);
    csvRows.push([`Period: ${timePeriodLabel}`]);
    const startDate = profitabilityData.dateRange?.startDate
      ? new Date(profitabilityData.dateRange.startDate).toLocaleDateString("fr-FR")
      : profitabilityData.calculationDetails?.dateRange?.startDate
      ? new Date(profitabilityData.calculationDetails.dateRange.startDate).toLocaleDateString("fr-FR")
      : "N/A";
    const endDate = profitabilityData.dateRange?.endDate
      ? new Date(profitabilityData.dateRange.endDate).toLocaleDateString("fr-FR")
      : profitabilityData.calculationDetails?.dateRange?.endDate
      ? new Date(profitabilityData.calculationDetails.dateRange.endDate).toLocaleDateString("fr-FR")
      : "N/A";
    csvRows.push([`Date Range: ${startDate} - ${endDate}`]);
    csvRows.push([]);

    // Summary Section
    csvRows.push(["SUMMARY"]);
    csvRows.push(["Metric", "Value (EUR)"]);
    csvRows.push(["Total Costs", formatCurrency(profitabilityData.totalCosts).replace("€", "").trim()]);
    csvRows.push(["Monthly Costs", formatCurrency(profitabilityData.monthlyCosts).replace("€", "").trim()]);
    csvRows.push(["Activity-based Costs", formatCurrency(profitabilityData.dailyCosts).replace("€", "").trim()]);
    csvRows.push(["Total Revenue (CA)", formatCurrency(profitabilityData.totalRevenue).replace("€", "").trim()]);
    csvRows.push(["Total Investment", formatCurrency(profitabilityData.totalInvestment).replace("€", "").trim()]);
    csvRows.push(["Total Benefit", formatCurrency(profitabilityData.totalBenefit).replace("€", "").trim()]);
    csvRows.push(["Total Margin (%)", profitabilityData.totalMargin.toFixed(2)]);
    csvRows.push([]);

    // Revenue by Type
    csvRows.push(["REVENUE BY BUDGET TYPE"]);
    csvRows.push(["Type", "Amount (EUR)"]);
    Object.entries(profitabilityData.revenueByType || {}).forEach(([type, amount]) => {
      csvRows.push([type, formatCurrency(amount).replace("€", "").trim()]);
    });
    csvRows.push([]);

    // Main Profitability Table (Matching Google Sheet Format)
    csvRows.push(["Person", "Project", "Cost €", "TJM Sell €", "TJM Overwritten €", "Days Worked", "Invoicable €", "Invoiced €", "Benefit €", "Margin %"]);

    if (profitabilityData.personProfitability && profitabilityData.personProfitability.length > 0) {
      profitabilityData.personProfitability.forEach((person) => {
        person.projectBreakdown.forEach((project) => {
          // Calculate invoicable revenue (what could be invoiced - using calculated revenue)
          const invoicableRevenue = project.calculatedRevenue;
          
          csvRows.push([
            person.name,
            project.projectName,
            Math.round(project.allocatedCost),
            Math.round(person.baseTjms || 0),
            Math.round(project.overwrittenTjms || person.baseTjms || 0),
            project.daysWorked.toFixed(1),
            Math.round(invoicableRevenue),
            Math.round(project.invoicedRevenue || 0),
            Math.round(project.benefit),
            project.margin.toFixed(1),
          ]);
        });
      });
    } else {
      csvRows.push(["No person data available"]);
    }
    csvRows.push([]);
    csvRows.push([]);

    // Invoice Tracker Section
    csvRows.push(["INVOICE TRACKER"]);
    csvRows.push(["Project", "Total Invoice €", "Billed So Far €", "Remaining €"]);

    // Group invoices by project directly from invoice projectId/projectName
    const projectInvoiceMap = new Map();
    const invoicesArray = profitabilityData.invoicesData || [];
    
    // Group invoices by their project
    invoicesArray.forEach((invoice) => {
      const projectName = invoice.projectName || "";
      const projectId = invoice.projectId || "";
      
      if (!projectName && !projectId) return;
      
      const key = projectName || projectId;
      
      if (!projectInvoiceMap.has(key)) {
        projectInvoiceMap.set(key, {
          projectName: projectName || projectId,
          totalInvoice: 0,
          billedSoFar: 0,
        });
      }
      
      const invoiceData = projectInvoiceMap.get(key);
      const invoiceTotal = invoice.total || 0;
      invoiceData.totalInvoice += invoiceTotal;
      
      // Billed so far = invoices that are sent or received
      if (invoice.sent === "yes" || invoice.received === "yes") {
        invoiceData.billedSoFar += invoiceTotal;
      }
    });
    
    // Output invoice tracker
    projectInvoiceMap.forEach((data) => {
      const remaining = data.totalInvoice - data.billedSoFar;
      
      csvRows.push([
        data.projectName,
        Math.round(data.totalInvoice),
        Math.round(data.billedSoFar),
        Math.round(remaining),
      ]);
    });
    
    if (projectInvoiceMap.size === 0) {
      csvRows.push(["No invoice data available"]);
    }

    // Revenue by Type per Member
    csvRows.push(["REVENUE BY TYPE PER MEMBER"]);
    const revenueTypes = ["project", "prediction", "admin", "invest", "overbudget", "leave"];
    const headerRow = ["Member Name", ...revenueTypes.map((t) => `${t} (EUR)`)];
    csvRows.push(headerRow);

    profitabilityData.teamMembers.forEach((member) => {
      const row = [member.name];
      revenueTypes.forEach((type) => {
        row.push(formatCurrency(member.revenueByType[type] || 0).replace("€", "").trim());
      });
      csvRows.push(row);
    });
    csvRows.push([]);

    // Occupation Data
    csvRows.push(["OCCUPATION DATA"]);
    csvRows.push(["Name", "Working Hours", "Investment Hours", "Available Hours", "Occupation Rate (%)", "Status"]);
    profitabilityData.occupationData.teamOccupation.forEach((member) => {
      csvRows.push([
        member.name,
        member.workingHours.toFixed(2),
        member.investmentHours.toFixed(2),
        member.availableHours.toFixed(2),
        member.occupationRate.toFixed(2),
        member.isActive ? "Active" : "Former",
      ]);
    });
    csvRows.push([
      "TEAM AVERAGE",
      profitabilityData.occupationData.totalWorkingHours.toFixed(2),
      "",
      profitabilityData.occupationData.totalAvailableHours.toFixed(2),
      profitabilityData.occupationData.averageOccupation.toFixed(2),
      "",
    ]);
    csvRows.push([]);

    // Project-Level Profitability
    csvRows.push(["PROJECT-LEVEL PROFITABILITY"]);
    csvRows.push([
      "Project Name",
      "Project ID",
      "Activities Count",
      "Total Hours",
      "Team Members",
      "Budget Types",
      "Calculated Revenue (EUR)",
      "Invoiced Revenue (EUR)",
      "Revenue Difference (EUR)",
      "Allocated Costs (EUR)",
      "Benefit (EUR)",
      "Margin (%)",
      "Invoiced Margin (%)",
      "Invoice IDs",
      "Member Contributions",
    ]);

    if (profitabilityData.projectProfitability && profitabilityData.projectProfitability.length > 0) {
      profitabilityData.projectProfitability.forEach((project) => {
        const memberContributions = project.memberContributions
          .map((contrib) => `${contrib.memberName}: ${contrib.hours.toFixed(1)}h`)
          .join("; ");
        
        csvRows.push([
          project.projectName,
          project.projectId || "",
          project.activitiesCount,
          project.totalHours.toFixed(2),
          project.teamMemberCount,
          project.budgetTypes.join(", "),
          formatCurrency(project.calculatedRevenue).replace("€", "").trim(),
          formatCurrency(project.invoicedRevenue).replace("€", "").trim(),
          formatCurrency(project.revenueDifference).replace("€", "").trim(),
          formatCurrency(project.allocatedCosts).replace("€", "").trim(),
          formatCurrency(project.benefit).replace("€", "").trim(),
          project.margin.toFixed(2),
          project.invoicedMargin.toFixed(2),
          project.invoiceIds.join(", "),
          memberContributions,
        ]);
      });
    } else {
      csvRows.push(["No project data available"]);
    }
    csvRows.push([]);

    // Project-Level Member Breakdown
    if (profitabilityData.projectProfitability && profitabilityData.projectProfitability.length > 0) {
      csvRows.push(["PROJECT MEMBER CONTRIBUTIONS"]);
      csvRows.push(["Project Name", "Member Name", "Hours", "Percentage of Project"]);
      
      profitabilityData.projectProfitability.forEach((project) => {
        project.memberContributions.forEach((contrib) => {
          const percentage = project.totalHours > 0 ? ((contrib.hours / project.totalHours) * 100).toFixed(2) : "0.00";
          csvRows.push([
            project.projectName,
            contrib.memberName,
            contrib.hours.toFixed(2),
            `${percentage}%`,
          ]);
        });
      });
      csvRows.push([]);
    }

    // Detailed Activities
    csvRows.push(["DETAILED ACTIVITIES"]);
    csvRows.push([
      "Date",
      "Member Name",
      "Project Name",
      "Project ID",
      "Budget Name",
      "Budget Type",
      "Hours",
      "Cost (EUR)",
      "Revenue (EUR)",
      "User TJM (Cost)",
      "User TJMs (Sell Price)",
      "Payment Type",
      "Contract",
      "Job Title",
      "Invoiced",
      "Freezed",
      "Paid",
      "Comment",
    ]);

    if (profitabilityData.allActivities && profitabilityData.allActivities.length > 0) {
      profitabilityData.allActivities.forEach((activity) => {
        const activityDate = activity.date ? new Date(activity.date).toLocaleDateString("fr-FR") : "";
        csvRows.push([
          activityDate,
          activity.userName || "",
          activity.projectName || "",
          activity.projectId || "",
          activity.budgetName || "",
          activity.budgetType || "",
          (activity.total || 0).toFixed(2),
          formatCurrency(activity.cost || 0).replace("€", "").trim(),
          formatCurrency(activity.value || 0).replace("€", "").trim(),
          activity.userTjm || "",
          activity.userTjms || "",
          activity.userPaymentType || "",
          activity.userContract || "",
          activity.userJobTitle || "",
          activity.invoiced || "",
          activity.freezed || "",
          activity.paid || "",
          activity.comment || "",
        ]);
      });
    }

    // Convert to CSV string
    const csvContent = csvRows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `profitability-report-${timePeriodLabel.replace(/\s+/g, "-").toLowerCase()}-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("CSV exported successfully");
  };

  if (loading) {
    return (
      <DashboardCard title="Profitability" icon={<FaEuroSign className="text-green-500" />}>
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard
      title="Profitability"
      icon={<FaEuroSign className="text-green-500" />}
      action={
        <div className="flex items-center gap-2">
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            className="text-xs px-2 py-1 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500">
            {getTimePeriodOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button onClick={() => setShowDetails(!showDetails)} className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
            {showDetails ? "Hide Details" : "Show Details"}
          </button>
          <button
            onClick={exportToCSV}
            disabled={loading || !profitabilityData.teamMembers || profitabilityData.teamMembers.length === 0}
            className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 disabled:bg-gray-100 disabled:cursor-not-allowed rounded-md transition-colors flex items-center gap-1">
            <FaDownload className="text-xs" />
            Export CSV
          </button>
        </div>
      }>
      <div className="space-y-3">
        {/* Main Metrics */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-red-50 p-3 rounded-lg">
            <h3 className="text-xs font-medium text-gray-600 mb-1">Total Costs</h3>
            <p className="text-lg font-bold text-red-600">{formatCurrency(profitabilityData.totalCosts)}</p>
            {showDetails && (
              <div className="text-xs text-gray-500 mt-1">
                <div>Monthly: {formatCurrency(profitabilityData.monthlyCosts)}</div>
                <div>Activity-based: {formatCurrency(profitabilityData.dailyCosts)}</div>
              </div>
            )}
          </div>
          <div className="bg-blue-50 p-3 rounded-lg">
            <h3 className="text-xs font-medium text-gray-600 mb-1">Revenue (CA)</h3>
            <p className="text-lg font-bold text-blue-600">{formatCurrency(profitabilityData.totalRevenue)}</p>
            {showDetails && <div className="text-xs text-gray-500 mt-1">Excludes admin & invest</div>}
          </div>
        </div>

        {/* Investment & Benefit */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-purple-50 p-3 rounded-lg">
            <h3 className="text-xs font-medium text-gray-600 mb-1">Investment</h3>
            <p className="text-lg font-bold text-purple-600">{formatCurrency(profitabilityData.totalInvestment)}</p>
            {showDetails && <div className="text-xs text-gray-500 mt-1">From invest budget type</div>}
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <h3 className="text-xs font-medium text-gray-600 mb-1">Benefit</h3>
            <p className={`text-lg font-bold ${getBenefitColor(profitabilityData.totalBenefit)}`}>{formatCurrency(profitabilityData.totalBenefit)}</p>
            {showDetails && <div className="text-xs text-gray-500 mt-1">Revenue - Costs</div>}
          </div>
        </div>

        {/* Occupation Data Box */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Team Occupation Distribution</h3>
          
          {/* Team Members Occupation List */}
          <div className="space-y-3">
            {profitabilityData.occupationData.teamOccupation.map((member, index) => {
              const occupationColor = member.occupationRate >= 80 ? '#10b981' : member.occupationRate >= 60 ? '#f59e0b' : '#ef4444';
              const workingDays = (member.workingHours / 8).toFixed(1);
              const availableDays = (member.availableHours / 8).toFixed(1);
              
              return (
                <div key={index} className="flex items-center gap-3">
                  {/* Colored Indicator */}
                  <div 
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: occupationColor }}
                  />
                  
                  {/* Member Name */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <img src={member.avatar} alt={member.name} className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 truncate">{member.name}</span>
                      {!member.isActive && <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full">Former</span>}
                    </div>
                  </div>
                  
                  {/* Primary Metrics (Top Right) */}
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900">{workingDays}d</div>
                    <div className="text-xs text-gray-500">{member.occupationRate.toFixed(1)}%</div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-24">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${Math.min(Math.max(member.occupationRate, 0), 100)}%`,
                          backgroundColor: occupationColor
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Secondary Metrics (Bottom) */}
                  <div className="text-right text-xs text-gray-500 min-w-0">
                    <div className="whitespace-nowrap">{member.workingHours.toFixed(1)}h worked</div>
                    {member.investmentHours > 0 && (
                      <div className="whitespace-nowrap text-gray-400">{member.investmentHours.toFixed(1)}h invest</div>
                    )}
                    <div className="whitespace-nowrap">{member.availableHours.toFixed(1)}h available</div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Team Summary */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-gray-600">Total Days:</span>
                  <span className="ml-1 font-medium">{(profitabilityData.occupationData.totalWorkingHours / 8).toFixed(1)}d</span>
                </div>
                <div>
                  <span className="text-gray-600">Active Members:</span>
                  <span className="ml-1 font-medium">{profitabilityData.occupationData.teamOccupation.filter(m => m.isActive).length}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">{profitabilityData.occupationData.averageOccupation.toFixed(1)}%</div>
                <div className="text-xs text-gray-500">Average Occupation</div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Revenue Breakdown */}
        {showDetails && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Revenue by Budget Type</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(profitabilityData.revenueByType).map(([type, amount]) => (
                <div key={type} className="flex justify-between">
                  <span className="capitalize">{type}:</span>
                  <span className="font-medium">{formatCurrency(amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Members Detailed Breakdown */}
        {showDetails && (
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Team Profitability by Person</h4>
            <div className="space-y-3">
              {profitabilityData.teamMembers.map((member, index) => (
                <div key={index} className={`p-3 rounded-lg border ${member.isActive ? "bg-white border-gray-100" : "bg-orange-50 border-orange-200"}`}>
                  {/* Member Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <img src={member.avatar} alt={member.name} className="w-6 h-6 rounded-full object-cover" />
                        {!member.isActive && <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full border border-white" title="Former employee" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-sm">{member.name}</span>
                          {!member.isActive && <span className="text-xs px-1 py-0.5 bg-orange-100 text-orange-700 rounded">Former</span>}
                        </div>
                        <div className="text-xs text-gray-500">
                          {member.costType} • {member.workingHours.toFixed(1)}h work
                          {member.leaveHours > 0 && ` + ${member.leaveHours.toFixed(1)}h leave`}
                        </div>
                        {member.workingPeriod.description !== "Full period" && (
                          <div className="text-xs text-orange-600 font-medium">
                            {member.workingPeriod.description} ({member.workingPeriod.daysWorked} days)
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-bold ${getBenefitColor(member.benefit)}`}>{member.margin.toFixed(1)}%</div>
                      <div className="text-xs text-gray-500">margin</div>
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Cost</div>
                      <div className="text-sm font-medium text-red-600">{formatCurrency(member.cost)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Revenue</div>
                      <div className="text-sm font-medium text-blue-600">{formatCurrency(member.billableRevenue)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Benefit</div>
                      <div className={`text-sm font-medium ${getBenefitColor(member.benefit)}`}>{formatCurrency(member.benefit)}</div>
                    </div>
                  </div>

                  {/* Revenue Breakdown */}
                  <div className="text-xs">
                    <div className="text-gray-600 mb-1">Revenue by type:</div>
                    <div className="grid grid-cols-3 gap-1">
                      {Object.entries(member.revenueByType).map(
                        ([type, amount]) =>
                          amount > 0 && (
                            <div key={type} className="flex justify-between">
                              <span className="capitalize text-gray-500">{type}:</span>
                              <span className="font-medium">{formatCurrency(amount)}</span>
                            </div>
                          ),
                      )}
                    </div>
                  </div>

                  {/* Activities Summary */}
                  <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                    {member.activitiesCount} activities • Total revenue: {formatCurrency(member.totalRevenue)}
                    {member.totalRevenue !== member.billableRevenue && <span> (billable: {formatCurrency(member.billableRevenue)})</span>}
                    {member.leaveHours > 0 && (
                      <div className="mt-1 text-orange-600">
                        Leave cost: {formatCurrency(member.leaveCost)} ({member.leaveHours.toFixed(1)}h)
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Team Summary */}
            <div className="mt-3 p-2 bg-gray-100 rounded text-xs">
              <div className="font-medium mb-1">Team Summary:</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-gray-600">Best performer:</span>
                  <span className="ml-1 font-medium">
                    {profitabilityData.teamMembers.length > 0 ? profitabilityData.teamMembers.reduce((best, member) => (member.margin > best.margin ? member : best)).name : "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Avg margin:</span>
                  <span className="ml-1 font-medium">
                    {profitabilityData.teamMembers.length > 0
                      ? (profitabilityData.teamMembers.reduce((sum, member) => sum + member.margin, 0) / profitabilityData.teamMembers.length).toFixed(1)
                      : "0"}
                    %
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Margin */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border-2 border-green-100">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Total Margin</h3>
          <div className="flex items-center justify-between">
            <span className={`text-2xl font-bold ${getMarginColor(profitabilityData.totalMargin)}`}>{profitabilityData.totalMargin.toFixed(1)}%</span>
            <div className="text-right">
              <div className="text-xs text-gray-500">Target: 30%+</div>
              <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    profitabilityData.totalMargin >= 30 ? "bg-green-500" : profitabilityData.totalMargin >= 15 ? "bg-yellow-500" : "bg-red-500"
                  }`}
                  style={{ width: `${Math.min(Math.max(profitabilityData.totalMargin, 0), 100)}%` }}
                />
              </div>
            </div>
          </div>
          {showDetails && (
            <div className="mt-3 text-xs text-gray-600 bg-white p-2 rounded">
              <div className="font-medium mb-1">Calculation Details ({profitabilityData.calculationDetails.timePeriod}):</div>
              <div>
                • Revenue: {formatCurrency(profitabilityData.totalRevenue)} (from {profitabilityData.calculationDetails.revenueActivities} billable activities)
              </div>
              <div>
                • Costs: {formatCurrency(profitabilityData.totalCosts)} ({profitabilityData.calculationDetails.monthlyEmployees} monthly +{" "}
                {profitabilityData.calculationDetails.dailyEmployees} daily employees{profitabilityData.calculationDetails.includesManager ? ", including manager" : ""})
              </div>
              {profitabilityData.calculationDetails.formerMembers > 0 && (
                <div className="text-gray-600">
                  • Team composition: {profitabilityData.calculationDetails.activeMembers} active + {profitabilityData.calculationDetails.formerMembers} former employees
                </div>
              )}
              <div>• Benefit: {formatCurrency(profitabilityData.totalBenefit)} = Revenue - Costs</div>
              <div>• Margin: {profitabilityData.totalMargin.toFixed(1)}% = (Benefit ÷ Revenue) × 100</div>
              {profitabilityData.calculationDetails.totalLeaveCost > 0 && (
                <div className="mt-1 text-orange-600">
                  • Leave costs: {formatCurrency(profitabilityData.calculationDetails.totalLeaveCost)} ({profitabilityData.calculationDetails.totalLeaveHours.toFixed(1)}h from{" "}
                  {profitabilityData.calculationDetails.leaveActivities} activities)
                </div>
              )}
              {profitabilityData.calculationDetails.membersWithAdjustments > 0 && (
                <div className="mt-1 text-purple-600">
                  • {profitabilityData.calculationDetails.membersWithAdjustments} team member(s) with period adjustments:
                  {profitabilityData.calculationDetails.adjustmentDetails.map((adj, idx) => (
                    <div key={idx} className="ml-2">
                      - {adj.name}: {adj.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
          <p>
            <span className="font-medium">Formula:</span> Margin = (Revenue - Costs) / Revenue × 100
          </p>
          <p className="mt-1">
            <span className="font-medium">Note:</span> Revenue excludes admin & invest activities
          </p>
          {showDetails && (
            <p className="mt-1">
              <span className="font-medium">Data:</span> {profitabilityData.calculationDetails.workActivities} work activities +{" "}
              {profitabilityData.calculationDetails.leaveActivities} leave activities from {profitabilityData.teamMembers.length} team members (excluded{" "}
              {profitabilityData.calculationDetails.totalActivities - profitabilityData.calculationDetails.validActivities} "Unavailable" entries)
            </p>
          )}
        </div>
      </div>
    </DashboardCard>
  );
};

export default ProfitabilityCard;
